import React from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * A reusable Checkbox component for forms, integrated with react-hook-form.
 * The label is positioned to the right of the checkbox.
 * Uses semantic color classes from shared configuration.
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - The field label text
 * @param {string} props.name - The field name (used for form registration and error display)
 * @param {Function} props.register - The react-hook-form register function
 * @param {Object} props.errors - The errors object from react-hook-form
 * @param {Function} [props.onChange] - Optional onChange handler (in addition to register)
 * @param {boolean} [props.disabled] - Whether the checkbox is disabled
 * @param {boolean} [props.defaultChecked] - Default checked state
 * @param {string} [props.className] - Additional CSS classes
 */
const CheckboxField = ({ 
    label, 
    name, 
    register, 
    errors, 
    className = '',
    ...props 
}) => {
    const colors = useColors();
    const semantic = colors.semantic;
    const { replace } = useTextReplacement();

    const hasError = errors && errors[name];
    const errorMessage = hasError ? errors[name].message : null;

    return (
        <div className={`flex items-start mb-2 ${className}`}>
            <input
                type="checkbox"
                id={name}
                {...(register ? register(name) : {})}
                className={`h-4 w-4 mt-1 text-primary-600 focus:ring-primary-500 ${semantic.formBorder} rounded mr-2`}
                aria-invalid={hasError ? 'true' : 'false'}
                aria-describedby={hasError ? `${name}-error` : null}
                {...props}
            />
            <div className="flex-1">
                <label htmlFor={name} className={`text-sm ${semantic.formLabel} cursor-pointer`}>
                    {replace(label)}
                </label>
                {hasError && (
                    <p id={`${name}-error`} className="mt-1 text-xs text-red-500" role="alert">
                        {errorMessage}
                    </p>
                )}
            </div>
        </div>
    );
};

CheckboxField.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    register: PropTypes.func,
    errors: PropTypes.object,
    className: PropTypes.string,
};

export default CheckboxField;

